/**
 * AI Product Mapping — the run slice's read-only restore reducers.
 *
 * Split out of `./runSlice` (like `./runSliceGroups`) to keep that file inside the size
 * budget; spread into the slice's `reducers` so action names and `runActions.*` call sites
 * are unchanged.
 *
 * These own the "open a past run" path: a stored-page walk that must render the grid from
 * the first page while padding to the server's row total, then trim whatever the pages
 * never delivered.
 */

import type { PayloadAction } from '@reduxjs/toolkit';
import { ROW_STATUS, RUN_MODES } from './lib/constants';
import { mappedColumns } from './lib/columnMapping';
import { DEFAULT_MATCH_POLICY } from './lib/matchPolicy';
import { mapDetailRunStatus } from './lib/runDetail';
import type { ColumnResolution, RunDetailRun } from './api/types';
import type { GridRow } from './lib/types';
import type { ProductMappingRunState } from './runSlice';
import { blankRow, makeAiReview, makeInitialState, makeRun } from './runSlice';

export const restoreReducers = {
  /** Fill file metadata resolved on demand from Files MS (re-runs/restores start from a
   *  bare fileId). Only provided pieces land, so a partial lookup can't blank a known value. */
  fileMetaHydrated: (
    state: ProductMappingRunState,
    { payload }: PayloadAction<{ name?: string; size?: number }>,
  ) => {
    if (payload.name) state.run.fileName = payload.name;
    if (payload.size) state.run.fileSizeBytes = payload.size;
  },

  /** Loading state while an opened run is resolved: `openRun` enters it before its
   *  live-vs-finished peek, and the read-only detail restore keeps it until the stored rows
   *  land (a live run moves onward via `runInitiated` instead). */
  runDetailRequested: (_state: ProductMappingRunState, { payload }: PayloadAction<string>) => ({
    ...makeInitialState(),
    phase: 'processing' as const,
    review: true,
    reviewLoading: true,
    run: { ...makeRun(), id: payload },
  }),

  /** First stored page: run meta + resolution + rows land together so the grid renders
   *  without waiting for the full page walk. Rows place by idx, and the grid pads to the
   *  stored-row total with pending placeholders that later pages (merged via `rowsMatched`)
   *  fill in. reviewLoading stays on until `runDetailFinished` — it keeps the download
   *  locked while the grid is incomplete. */
  runDetailStarted: (
    state: ProductMappingRunState,
    {
      payload,
    }: PayloadAction<{
      run?: RunDetailRun | null;
      resolution?: ColumnResolution | null;
      rows?: GridRow[];
      /** The server's stored-row total — the size the placeholder grid pads to. */
      total?: number;
    }>,
  ) => {
    const run = payload.run || ({} as RunDetailRun);
    const totals = run.totals || null;
    // A parked product-first run persists as plain `completed` (the worker is gone; only
    // group review remains) — the pending tally is what marks it parked. Restore it as
    // awaiting_products so the leave guard keeps protecting the review session.
    const mappedStatus = mapDetailRunStatus(run.status);
    const status =
      mappedStatus === 'complete' && run.mode === RUN_MODES.PRODUCT_FIRST && (totals?.pending ?? 0) > 0
        ? ('awaiting_products' as const)
        : mappedStatus;
    const rows: GridRow[] = [];
    for (const row of payload.rows || []) {
      const i = row.idx - 1;
      if (i < 0) continue;
      while (rows.length <= i) rows.push(blankRow(rows.length + 1));
      rows[i] = row;
    }
    while (rows.length < (payload.total || 0)) rows.push(blankRow(rows.length + 1));
    state.review = true;
    state.reviewLoading = true;
    state.reviewError = null;
    // A fresh restore starts clean: the loaded rows' flags are persisted state, not
    // this session's work — only new local marks re-raise the leave-guard's signal.
    state.rowsTouched = false;
    // A run reloaded mid-AI-review restores its thinking state; a finished review its
    // tallies (the per-group suggestions ride the groups read).
    state.aiReview = run.aiReview
      ? {
          status:
            run.aiReview.status === 'processing'
              ? 'running'
              : run.aiReview.status === 'failed'
                ? 'failed'
                : 'done',
          attempted: 0,
          total: run.aiReview.total ?? 0,
          suggested: run.aiReview.suggested ?? 0,
          none: run.aiReview.none ?? 0,
          unanswered: run.aiReview.unanswered ?? 0,
          error: run.aiReview.error ?? null,
        }
      : makeAiReview();
    state.phase = 'processing';
    state.rows = rows;
    state.columnResolution = payload.resolution || null;
    state.headers = payload.resolution?.headers || [];
    state.detected = payload.resolution ? mappedColumns(payload.resolution) : [];
    state.policy = { ...DEFAULT_MATCH_POLICY };
    // fileName comes from the persisted run record when present; older runs that stored
    // only the fileId fall back to the parallel Files-MS lookup (fileMetaHydrated), and the
    // strip shows the fileId until either lands. processedCount stays 0 so the progress bar
    // tracks the rows actually loaded while the stored pages stream in.
    state.run = {
      ...makeRun(),
      id: run.id || state.run.id,
      status,
      mode: run.mode ?? null,
      context: run.context || null,
      catalogType: run.context?.source ? String(run.context.source).toLowerCase() : null,
      fileId: run.fileId || null,
      fileName: run.fileName ?? null,
      fileSizeBytes: run.fileSizeBytes || 0,
      // The server's true file total — the progress denominator for a restored run that's
      // still running (parked/mid-flight) reads correctly before every page has streamed in.
      // Legacy runs stored no sourceRowCount, so fall back to the finished-row total, then to
      // the stored-row page total; 0 only when none of those is known.
      rowCount: run.sourceRowCount || totals?.rows || payload.total || 0,
      // Row-first restores leave this 0 so the progress bar tracks the stored pages as
      // they stream in. Product-first restores are row-lazy — the grid never fills — so
      // the run's own processed total is the numerator, not the loaded-row count (which
      // read "519 / 28,506" on a completed run).
      processedCount: run.mode === RUN_MODES.PRODUCT_FIRST ? totals?.rows || 0 : 0,
      totals,
      truncated: Boolean(run.truncated),
      errorMessage: run.error || null,
    };
  },

  /** The restore walk is done: unlock the review (download included) and drop trailing
   *  placeholders the server's total promised but the pages never delivered, so phantom
   *  pending rows don't linger in the tallies. Product-first restores keep theirs — the
   *  walk deliberately stops after one page there, and the placeholders are the grid
   *  slots the (lazily fetched) group rows land in. */
  runDetailFinished: (state: ProductMappingRunState) => {
    state.reviewLoading = false;
    if (state.run.mode === RUN_MODES.PRODUCT_FIRST) return;
    while (state.rows.length) {
      const last = state.rows[state.rows.length - 1]!;
      if (last.status !== ROW_STATUS.PENDING || last.raw.length > 0) break;
      state.rows.pop();
    }
  },

  runDetailFailed: (
    state: ProductMappingRunState,
    { payload }: PayloadAction<{ message?: string } | undefined>,
  ) => {
    state.reviewLoading = false;
    state.reviewError = payload?.message || 'Could not load the run details.';
  },
};
